//import React, { useState, useMemo } from "react";
import React, { useState, useMemo, useEffect } from "react";
import { ReportAPI } from "../../api/apiClient";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

interface Props {
  projectId: number;
  projectName: string;
  //projectStartDate: string;
}

export default function GroupProgressRangeReport({
  projectId,
  projectName,
 // projectStartDate
}: Props) {

  const [qDate, setQDate] = useState("");
  //const [qDate, setQDate] = useState(projectStartDate);
  const [endDate, setEndDate] = useState(
    new Date().toISOString().split("T")[0]
  );

  const [usedQDate, setUsedQDate] = useState("");
  const [usedEndDate, setUsedEndDate] = useState("");

  const [lvl] = useState(0);
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadProjectStartDate = async () => {
  try {
    const response = await ReportAPI.getProjectStartDate(projectId);
    setQDate(response.startDate);
  } catch (err) {
    console.error(err);
  }
};

useEffect(() => {
  loadProjectStartDate();
}, [projectId]);

  const loadReport = async () => {
    setError("");
    setLoading(true);
    setData([]);

    try {
      const response = await ReportAPI.getGroupProgressRangeReport({
        wbs: -1 * projectId,
        qDate: qDate || null,
        endDate: endDate || null,
        lvl: lvl
      });

      // ✅ IMPORTANT (Backend returns capital keys)
      /*setData(response?.Data ?? []);
      setUsedQDate(response?.UsedQDate ?? "");
      setUsedEndDate(response?.UsedEndDate ?? "");*/
      setData(response?.data ?? []); 
      setUsedQDate(response?.usedQDate ?? ""); 
      setUsedEndDate(response?.usedEndDate ?? "");

    } catch (err: any) {
      setError(err.message || "Failed to load report");
    } finally {
      setLoading(false);
    }
  };

  // =============================
  // GROUP + SORT
  // =============================
  const groupedData = useMemo(() => {

    if (!data || data.length === 0) return [];

    const groups: any = {};

    const getMainNumber = (name: string) => {
      const match = name?.match(/^\d+/);
      return match ? parseInt(match[0]) : 999;
    };

    const getSubNumber = (name: string) => {
      const match = name?.match(/^\d+(\.\d+)?/);
      return match ? parseFloat(match[0]) : 999;
    };

    data.forEach((row) => {
      const mainGroup = row.levelName;

      if (!groups[mainGroup]) {
        groups[mainGroup] = {
          main: mainGroup,
          items: []
        };
      }

      groups[mainGroup].items.push(row);
    });

    Object.values(groups).forEach((group: any) => {
      group.items.sort(
        (a: any, b: any) =>
          getSubNumber(a.workGroup) - getSubNumber(b.workGroup)
      );
    });

    return Object.values(groups).sort(
      (a: any, b: any) =>
        getMainNumber(a.main) - getMainNumber(b.main)
    );

  }, [data]);

  // =============================
  // Formatting Helpers
  // =============================
  const formatPrevious = (value: number) =>
    value && value !== 0 ? (value * 100).toFixed(2) + "%" : "";

  const formatAchieved = (value: number) =>
    value && value !== 0 ? (value * 100).toFixed(2) + "%" : "-";

  // =============================
  // EXPORT PDF
  // =============================
  const exportPDF = () => {

    if (!groupedData.length) return;

    const doc = new jsPDF("p", "mm", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    let y = 12;

    // Title
    doc.setFontSize(14);
    doc.text("Group Progress Report", pageWidth / 2, y, { align: "center" });
    y += 8;

    doc.setFontSize(10);
    doc.text(`Project: ${projectName}`, 14, y);
    y += 6;

    if (usedQDate && usedEndDate) {
      doc.text(
        `Period: ${new Date(usedQDate).toLocaleDateString()} - ${new Date(
          usedEndDate
        ).toLocaleDateString()}`,
        14,
        y
      );
      y += 6;
    }

    // Build Table
    const body: any[] = [];

    groupedData.forEach((group: any) => {

      body.push([
        {
          content: group.main,
          colSpan: 4,
          styles: { fillColor: [220, 220, 220], fontStyle: "bold" }
        }
      ]);

      group.items.forEach((row: any) => {
        body.push([
          row.workGroup,
          row.transaction || "",
          formatPrevious(row.previous),
          formatAchieved(row.achieved)
        ]);
      });

    });

    autoTable(doc, {
      startY: y,
      head: [["Work", "Unit", "Previous", "Achieved"]],
      body: body,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [55, 65, 81] },
      margin: { left: 14, right: 14 }
    });

    // Footer (Page Numbers + Generated Date)
    const pageCount = doc.getNumberOfPages();

    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);

      doc.setFontSize(8);

      doc.text(
        `Page ${i} of ${pageCount}`,
        pageWidth / 2,
        pageHeight - 8,
        { align: "center" }
      );

      doc.text(
        `Report Generated On: ${new Date().toLocaleString()}`,
        pageWidth - 14,
        pageHeight - 8,
        { align: "right" }
      );
    }

    doc.save("GroupProgressReport.pdf");
  };

  return (
    <div className="w-full min-h-screen bg-white px-10 py-6 text-sm">

      <h2 className="text-center font-semibold mb-4">
        Group Progress Report
      </h2>

      {usedQDate && (
        <div className="text-center mb-6">
          <div><strong>Project:</strong> {projectName}</div>
          <div>
            <strong>Period:</strong>{" "}
            {new Date(usedQDate).toLocaleDateString()} -{" "}
            {new Date(usedEndDate).toLocaleDateString()}
          </div>
        </div>
      )}

      <div className="flex gap-4 mb-6 justify-center">
        <input
          type="date"
          value={qDate}
          onChange={(e) => setQDate(e.target.value)}
          className="border px-3 py-1"
        />
        <input
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          className="border px-3 py-1"
        />

        <button
          onClick={loadReport}
          className="bg-blue-600 text-white px-5 py-1"
        >
          Load Report
        </button>

        <button
          onClick={exportPDF}
          className="bg-green-600 text-white px-5 py-1"
        >
          Export PDF
        </button>
      </div>

      {loading && <div className="text-center">Loading...</div>}
      {error && <div className="text-red-600 text-center">{error}</div>}

      {!loading && groupedData.map((group: any, index: number) => (

        <div key={index} className="mb-6 w-full">

          <div className="flex w-full bg-gray-700 text-white font-semibold py-2 px-4">
            <div className="flex-[3]">Work</div>
            <div className="flex-[1]">Unit</div>
            <div className="flex-[1] text-right">Previous</div>
            <div className="flex-[1] text-right">Achieved</div>
          </div>

          <div className="w-full bg-gray-300 font-semibold px-4 py-2">
            {group.main}
          </div>

          {group.items.map((row: any, idx: number) => (
            <div
              key={idx}
              className="flex w-full px-4 py-2 border-b border-gray-200"
            >
              <div className="flex-[3] pl-6">
                {row.workGroup}
              </div>

              <div className="flex-[1]">
                {row.transaction || ""}
              </div>

              <div className="flex-[1] text-right">
                {formatPrevious(row.previous)}
              </div>

              <div className="flex-[1] text-right">
                {formatAchieved(row.achieved)}
              </div>

            </div>
          ))}

        </div>

      ))}

    </div>
  );
}